import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTeamSearch } from '@/hooks/useTeamSearch';

interface TeamSearchComboboxProps {
  value?: number | string;
  onSelect: (teamId: number | string | null, teamName: string) => void;
  placeholder?: string;
  className?: string;
  allowTBD?: boolean;
}

export const TeamSearchCombobox = ({ 
  value, 
  onSelect, 
  placeholder = "Select team...",
  className,
  allowTBD = true
}: TeamSearchComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { teams, loading } = useTeamSearch();

  const selectedTeam = teams.find(team => team.team_id === value);
  const displayValue = value === 'tbd' ? 'TBD (To Be Determined)' : 
                      selectedTeam ? selectedTeam.team_name : '';

  const filteredTeams = useMemo(() => {
    if (!searchTerm) return teams;
    return teams.filter(team => 
      team.team_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.coach_name && team.coach_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [teams, searchTerm]);

  const handleSelect = (teamId: number | string, teamName: string) => {
    console.log('Team selected:', { teamId, teamName });
    onSelect(teamId, teamName);
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            className
          )}
        >
          {displayValue || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-full p-0 bg-background border shadow-lg z-[99999]" 
        sideOffset={4}
        align="start"
      >
        <div className="p-3">
          <Input
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        
        <ScrollArea className="max-h-60">
          <div className="p-1">
            {loading ? (
              <div className="p-4 text-center text-muted-foreground">
                Loading teams...
              </div>
            ) : (
              <>
                {allowTBD && (
                  <div
                    onClick={() => handleSelect('tbd', 'TBD (To Be Determined)')}
                    className="flex items-center px-3 py-2 cursor-pointer rounded-sm hover:bg-accent transition-colors"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === 'tbd' ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="font-medium">TBD (To Be Determined)</span>
                      <span className="text-sm text-muted-foreground">
                        Team will be assigned later
                      </span>
                    </div>
                  </div>
                )}
                
                {filteredTeams.length === 0 && !loading ? (
                  <div className="p-4 text-center text-muted-foreground">
                    No teams found
                  </div>
                ) : (
                  filteredTeams.map((team) => (
                    <div
                      key={team.team_id}
                      onClick={() => handleSelect(team.team_id, team.team_name)}
                      className="flex items-center px-3 py-2 cursor-pointer rounded-sm hover:bg-accent transition-colors"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === team.team_id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{team.team_name}</span>
                        {team.coach_name && (
                          <span className="text-sm text-muted-foreground">
                            Coach: {team.coach_name}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};